import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { GatingContext } from '../core/context';

import cx from 'classnames'
import { animate, motion, useMotionValue } from 'framer-motion';
import { useMediaQuery } from 'react-responsive';
import { IKImage } from 'imagekitio-react'
import { ScrollMenu } from 'react-horizontal-scrolling-menu';
import { Button } from 'react-bootstrap';
import { HotKeys, GlobalHotKeys } from 'react-hotkeys'
import TruncatedTextWithTooltip from '../../../components/TruncatedTextWithTooltip';
import _ from 'lodash';
import { ParallaxProvider } from 'react-scroll-parallax';
import { isMobile } from 'react-device-detect';

import { BackgroundViewComponent, VideoView } from '../background';``
import { colorToString, injectAktivFallback } from '../widget.helper';
import { getColorsConfig } from '../../../components/AssetViewer/helper/cssHelper';

import styles from './spatial.module.scss';
import { useSelector } from 'react-redux';
import { SLIDE } from '../../../constants/widgetTypes';
import { SCALE } from '../../../constants/background';
import { useDispatch } from 'react-redux';
import { updateHyperLinkSlide } from '../../../actions/spatial';
import { WidgetsContext } from '../../../index';


const DESKTOP_ASPECT_RATIO = 16 / 9;
const MOBILE_ASPECT_RATIO = 9 / 16;
const DESKTOP_WIDTH_PADDING = 160;
const MOBILE_WIDTH_PADDING = 20;
const DESKTOP_HEIGHT_PADDING = 120;
const MOBILE_HEIGHT_PADDING = 60;
const HIGHLIGHT_WIDTH = 225;

const PAGES_TO_LOAD = 5;
const PAGES_TO_RENDER = 3;

const transition = {
  ease: 'easeOut',
  bounce: 0,
  duration: 0.2
}

export const useDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(true); // Default to true for SSR
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(min-width: 992px)');
      setIsDesktop(mediaQuery.matches);
      const handler = () => setIsDesktop(mediaQuery.matches);
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    }
  }, []);

  return mounted ? isDesktop : true; // Return true during SSR
};


export const useMobilePortrait = () => {
  const [isMobilePortrait, setIsMobilePortrait] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        setIsMobilePortrait(window.innerHeight > window.innerWidth);
      }
    };

    // Initial check on component mount
    handleResize();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  // Return consistent value during SSR
  if (!mounted) {
    return false;
  }

  return isMobilePortrait;
};

export const SpatialContent = React.memo(({
  breakpoint,
  initialPage,
  pages,
  appearance,
  onChange,
  children,
  onFetchPage,
  imageCDN,
  updatePagesToRender
}) => {

  const containerRef = React.useRef(null)
  const trackRef = useRef(null);
  const spatialPageRef = useRef(null);
  const spatialPageInnerRef = useRef(null);

  const singlePage = pages?.length == 1;
  const mobilePotrait = useMobilePortrait();
  const mobile = isMobile;

  const [state, setState] = useState({
    initialized: false,
    dimensions: { width: 0, height: 0 },
    visualViewport: '100vh',
    scale: 1,
    gating: null,
    menuPosition: { x: 0, y: 0 },
    singlePage: false,
    showHighlight: false,
    showMenu: false,
    trackWidth: 0,
    dragConstraints: { left: 0, right: 0 },
    fullscreen: false,
    backgroundPosition: 0,
    activePage: {
      id: initialPage,
      fullscreen: singlePage,
      slideIndex: 0
    },
    mobilePotrait
  })
  
  const [width, setWidth] = useState(0);
  const [mounted, setMounted] = useState(false);

  const x = useMotionValue(0)

  const viewportSize = useViewportSize();
  const desktop = useDesktop();

  const aspectRatio = !mobilePotrait ? DESKTOP_ASPECT_RATIO : MOBILE_ASPECT_RATIO;
  const widthPadding = desktop ? DESKTOP_WIDTH_PADDING : MOBILE_WIDTH_PADDING;
  const heightPadding = desktop ? DESKTOP_HEIGHT_PADDING : MOBILE_HEIGHT_PADDING;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setState({
      ...state,
      mobilePotrait,
    })
  }, [mobilePotrait]) 

  React.useEffect(() => {
    if (!mounted) return;
    
    const timer = setTimeout(() => {
      setState(state => ({
        ...state,
        initialized: true
      }));
    }, 1000)

    onChange('index', {
      id: state.activePage.id,
      slideIndex: 0
    });

    return () => clearTimeout(timer);
  }, [mounted])

  React.useEffect(() => {
    if (initialPage) {
      setState(state => {
        return {
          ...state,
          activePage: {
            ...state.activePage,
            id: initialPage,
          }
        }
      })
    }
  }, [initialPage])

  React.useEffect(() => {
    if (!mounted) return;
    handleResize(state.showHighlight);
  }, [viewportSize, state.showHighlight, state.fullscreen, mounted]);

  React.useEffect(() => {
    if (!mounted) return;
    
    const menuElement = document.getElementById('cs-spatial-menu');
    if (state.dimensions.width === 0 || !spatialPageInnerRef.current || !menuElement) {
      return;
    }

    const menuRect = menuElement.getBoundingClientRect();
    const spatialPageRect = spatialPageRef.current.getBoundingClientRect();
    const spatialPageInnerRect = spatialPageInnerRef.current.getBoundingClientRect();

    const highlightX = spatialPageInnerRect.width + 80;
    const highlightY = spatialPageInnerRect.top;

    const widthDiff = spatialPageRect.width - spatialPageInnerRect.width;
    const spatialPageCenterX = state.showHighlight ? spatialPageInnerRect.width / 2 + 50 : spatialPageInnerRect.width / 2 + widthDiff / 2;
    const menuX = spatialPageCenterX - menuRect.width / 2;
    const menuY = state.fullscreen ? spatialPageInnerRect.bottom - (menuRect.height + 20) : spatialPageInnerRect.bottom - menuRect.height / 2;

    setState(state => ({
      ...state,
      menuPosition: {
        x: menuX,
        y: menuY
      },
      highlight: {
        ...state.highlight,
        x: highlightX,
        y: highlightY
      }
    }));

  }, [state.dimensions, spatialPageInnerRef.current, state.initialized, mounted]);

  React.useEffect(() => {
    if (!mounted) return;
    
    const controls = animate(x, calculateNewX(), transition)
    calculateBackgroundPosition();

    handleLazyLoadPages();
    handleUpdatePagesToRender();

    return () => {
      controls.stop()
    }
  }, [state.activePage.id, state.initialized, mounted]);

  const handleLazyLoadPages = () => {
    const activeIndex = pages.findIndex(obj => obj.id === state.activePage.id);

    if(activeIndex < 0) {
      return;
    }

    let closestIndices = getClosestIndices(pages, activeIndex, PAGES_TO_LOAD);
    closestIndices = [...closestIndices, activeIndex];

    const closestPages = closestIndices.map(index => pages[index]);
    closestPages.forEach(page => {
      if (page.loading || page.loaded) {
        return
      }

      if (onFetchPage) {
        onFetchPage(page.id);
      }
    });
  }

  const handleUpdatePagesToRender = () => {
    const activeIndex = pages.findIndex(obj => obj.id === state.activePage.id);
    const closestIndices = getClosestIndices(pages, activeIndex, PAGES_TO_RENDER);

    var closestPages = closestIndices.map(index => {
      const closestPage = pages[index];
      return closestPage.id;
    });

    var closestPages = [...closestPages, state.activePage.id];
    const uniqueClosestPages = _.uniq(closestPages);
    if(updatePagesToRender){
      updatePagesToRender(uniqueClosestPages, state.activePage.id)
    }
  }

  const getClosestIndices = (pages, index, limit) => {
    const result = [];
    const arrLength = pages.length;

    if (arrLength <= limit) {
      return pages.map((_, i) => i);
    }

    for (let i = index - 1; i >= 0 && result.length < limit / 2; i--) {
      result.unshift(i);
    }

    for (let i = index + 1; i < arrLength && result.length < limit; i++) {
      result.push(i);
    }

    for (let i = index - 2; i >= 0 && result.length < limit; i--) {
      result.unshift(i);
    }

    return result;
  }

  const handleResize = useCallback((showHighlight) => {
    if (!mounted || !viewportSize || viewportSize.width === 0) return;
    
    const { width, height, viewportHeight } = viewportSize;

      const calculateAvailableWidth = () => {
        if(state.fullscreen) {
          return width;
        }

        return showHighlight ? width - ((HIGHLIGHT_WIDTH * state.scale) + 120) : width - widthPadding;
      }

      let newHeightWidth = height;
      let newWidthHeight = width;

      if(!state.fullscreen) {
        const availableWidth = calculateAvailableWidth();
        const availableHeight = showHighlight || state.fullscreen ? height : height - heightPadding;

        newHeightWidth = (availableWidth) / aspectRatio;
        newWidthHeight = availableWidth;

        if (newHeightWidth > availableHeight) {
          newWidthHeight = (availableHeight) * aspectRatio;
          newHeightWidth = availableHeight;
        }
      }

      const scale = newHeightWidth == 1024 ? (newHeightWidth / 500) :  (newHeightWidth / 675);

      setState(state => ({
        ...state,
        scale: scale,
        showHighlight: showHighlight,
        viewportHeight: viewportHeight,
        dimensions: {
          width: newWidthHeight,
          height: newHeightWidth
        },
        trackWidth: (containerRef.current?.scrollWidth - containerRef.current?.offsetWidth) || 0
      }));
  }, [viewportSize, state.showHighlight, state.fullscreen, mounted]);

  useEffect(() => {
    if (!mounted) return;
    
    const updateDragConstraints = () => {
      const containerWidth = containerRef.current?.clientWidth || 0;
      const activeIndex = pages.findIndex((obj) => obj.id === state.activePage.id);
      const constraint = -activeIndex * containerWidth;
      setState((state)=>{
        return {
          ...state,
          dragConstraints: { left: constraint, right: constraint }
        }
      })
    };

    updateDragConstraints();
  }, [state.activePage.id, state.dimensions, mounted]);

  const toggleGridMenu = () => {
    setState(state => {
      return {
        ...state,
        showMenu: !state.showMenu,
        showHighlight: false
      }
    })
  }

  const navigateToPage = (id, fullscreen, slideIndex = 0) => {
    const previousId = state.activePage.id;
    var currentSlideIndex = slideIndex;
    if(previousId != id) {
      currentSlideIndex = 0;
    }

    setState(state => {
      return {
        ...state,
        fullscreen: fullscreen,
        activePage: {
          ...state.activePage,
          id: id,
          fullscreen: fullscreen,
          slideIndex: currentSlideIndex
        }
      }
    })

    if(onChange && previousId !== id) {
      onChange('index', {
        id: id,
        slideIndex: currentSlideIndex
      });
    }

    return Promise.resolve();
  }

  const triggeredGating=(gating)=>{
    setState((state)=>{
      return {
        ...state,
        gating:gating
      }
    })
  }

  const handleDragEnd =(event, info)=> {
    if(!info || state.fullscreen || state.gating) {
      return
    }

    const activeIndex = pages.findIndex((obj) => obj.id === state.activePage.id);
    const {offset} = info;

    if(_.gt(offset.x, 0) && activeIndex > 0){
      const prevPage = pages[(activeIndex - 1)]
      navigateToPage(_.get(prevPage, 'id'), state.fullscreen, 0)
    }
    else if(_.lt(offset.x, 0) && activeIndex < (_.size(pages) - 1 )) {
      const nextPage = pages[(activeIndex + 1)]
      navigateToPage(_.get(nextPage, 'id'), state.fullscreen, 0)
    }
  }

  const calculateBackgroundPosition = () => {
    if (!mounted) return;
    
    const backgroundImage = document.getElementById('cs-spatial-bg');
    if(!backgroundImage) {
      return;
    }

    const backgroundImageRect = backgroundImage.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();
    
    if (!containerRect) return;

    const activeIndex = pages.findIndex(obj => obj.id === state.activePage.id);
    const position = (backgroundImageRect.width - containerRect.width) / pages?.length * activeIndex;

    setWidth(backgroundImageRect.width);
    setState(state => {
      return {
        ...state,
        backgroundPosition: position
      }
    })
  }

  const calculateNewX = () => {
    const activeIndex = pages.findIndex(obj => obj.id === state.activePage.id);
    const offset = -(activeIndex * (containerRef.current?.clientWidth || 0));
    return offset || 0
  }

  const handleToggleFullscreen = () => {
    setState(state => {
      return {
        ...state,
        fullscreen: !state.fullscreen
      }
    })
  }

  if (!mounted) {
    return (
      <div style={{ height: '100vh' }} className={cx(styles.carousel)}>
        <div className={styles.loading}>
          <div className={styles.loader}>
            <video loop autoPlay playsInline muted>
              <source src='/images/shortLoader.mp4' type='video/mp4' />
            </video>
            <label>Loading Spatial content...</label>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SpatialContext.Provider value={
      {
        ...state,
        breakpoint: breakpoint,
        pages: pages,
        appearance: appearance,
        triggeredGating: triggeredGating,
        navigateToPage: navigateToPage,
        onToggleFullscreen: handleToggleFullscreen,
        onChange: onChange,
        pages: pages
      }
    }>
      <KeyManager>
        <motion.div
          key={breakpoint}
          ref={containerRef}
          style={{ height: state.viewportHeight }}
          className={cx(styles.carousel, {
            [styles.singlePage]: false
          })}
        >
          {!state.initialized && (
            <div className={styles.loading}>
              <div className={styles.loader}>
                <video loop autoPlay playsInline muted>
                  <source src='/images/shortLoader.mp4' type='video/mp4' />
                </video>
                <label>Loading Spatial content...</label>
              </div>
            </div>
          )}

          {/* <Highlight
            position={state.highlight}
            show={state.showHighlight}
            showGridMenu={state.showMenu}
            scale={state.scale} /> */}
          <Menu
            showGridMenu={state.showMenu}
            appearance={appearance}
            scale={state.scale}
            menuPosition={state.menuPosition}
            onToggleHighlight={handleResize.bind(this, !state.showHighlight)}
            onToggleGridMenu={toggleGridMenu}
            onToggleFullscreen={handleToggleFullscreen}
            activePage={state.activePage}
            navigateToPage={navigateToPage}
            breakpoint={breakpoint}
            pages={pages}
            fullscreen={state.fullscreen}
          />
          <div className={styles.backgroundOverlay}>
            { _.get(appearance, 'background.type', null) == 'color' &&
              <BackgroundViewComponent background={_.get(appearance, 'background', null)}/>
            }
            { _.get(appearance, 'background.type', null) !== 'color' &&
              <BackgroundManager
                background={_.get(appearance, 'background', null)}
                backgroundPosition={state.backgroundPosition}
                imageCDN={imageCDN}
                width={width}
                mobile={mobile}
                />
            }
          </div>

          <motion.div
            className={cx(styles.track)}
            ref={trackRef}
            style={{
              x
            }}
            whileTap={{ cursor: "grabbing" }}
            initial={false}
            dragConstraints={state.dragConstraints}
            draggable
            drag={breakpoint === 'mobile'? 'x': false}
            dragElastic={0.1}
            onDragEnd={(event, info) => {
              handleDragEnd(event, info)
            }}
          >
            <ParallaxProvider>
              <div className={cx(styles.spatialPage, styles.spatialPagePlaceholder)} ref={spatialPageRef}>
                <div className={styles.spatialPagePlaceholderInner} ref={spatialPageInnerRef}
                  style={{
                  width: state.dimensions.width, height: state.dimensions.height,
                  transformOrigin: 'center top'
                }}></div>
              </div>
              {React.Children.map(children, (child, i) => {
                return React.cloneElement(child)
              })}
            </ParallaxProvider>
          </motion.div>
          <GridMenu
            pages={pages}
            show={state.showMenu}
            onClose={toggleGridMenu}
            fullscreen={state.fullscreen}
            onSelect={navigateToPage}
          />
        </motion.div>
      </KeyManager>
    </SpatialContext.Provider>
  )
});

/**
 * Spatial Context
 */
export const SpatialContext = React.createContext({
  showMenu: false,
  showHighlight: false,
  fullscreen: false,
  dimensions: { width: 0, height: 0 }
});

SpatialContent.SpatialContext = SpatialContent;

/**
 * Spatial Page
 */
export const SpatialPage = React.memo(({appearance, page, children, slideWidgets=[], loading=null }) => {

  const dispatch = useDispatch();

  const spatialPageRef = React.useRef(null);
  const spatialContext = React.useContext(SpatialContext);
  const gatingContext = React.useContext(GatingContext);
  const gatingConfigurations = _.get(gatingContext, 'configurations', [])
  const slideBackground = _.get(appearance, 'slideBackground', {});
  const breakpoint = spatialContext.breakpoint;
  const widgetContext = React.useContext(WidgetsContext);

  const currentSlideIndexRef = useRef(_.get(spatialContext, 'activePage.slideIndex', 0));
  const startYRef = useRef(0);
  const fullscreenRef = useRef(spatialContext.fullscreen);
  const activePageRef = useRef(spatialContext.activePage);
  const slideWidgetsRef = useRef(slideWidgets);

  const slideDetails = useSelector((state) => _.get(state, `pages.spatial.hyperlinkSlide`, {}));
  const pages = useSelector((state) => _.get(state, 'pages.spatial.items', []));

  const activePageId = activePageRef.current?.id || "";

  const carouselSlide = _.get(widgetContext, 'carouselSlide', null) || null;

  const url = useMemo(() => {
    return pages.find(obj => obj.id === slideDetails.page)?.canonical || "";
  }, [slideDetails.page, pages]);
  
  const editorWidgets = useSelector(
    state => {
      const fromEditors = _.get(state, `editors.${url}.widgets`);
      if (fromEditors) return _.get(state, `editors.${url}.widgets`, []);
      return _.get(state, `${widgetContext?.selector}.widgets`) || [];
    },
    // deep equality so component doesn't rerender for referential-only changes
    _.isEqual
  );

  const slideMemoIndex = useMemo(() => {
    try {
      const uid = (editorWidgets || [])?.find(obj => obj.uid === slideDetails?.uid)?.uid;
      if(uid) {
        const sortedWidgets = (editorWidgets || [])?.filter((d) => d.type === SLIDE).sort((a, b) => a.order - b.order);
        const index = (sortedWidgets || [])?.findIndex(obj => obj.uid === uid);
        return index;
      }
      return -1;
    } catch {
      return -1;
    }
  }, [JSON.stringify(editorWidgets), slideDetails?.uid]);

  const widgetSlideIndex = useMemo(() => {
    try {
      const index = (slideWidgets || [])?.findIndex(obj => obj.uid === slideDetails?.uid);
      return index;
    } catch {
      return -1;
    }
  }, [JSON.stringify(slideWidgets), slideDetails?.uid]);

  useEffect(() => {
    slideWidgetsRef.current = slideWidgets;
  }, [slideWidgets]);

  useEffect(() => {
    if (!slideDetails.page || loading) return;

    if(slideDetails.page === activePageId && slideDetails.slide && slideMemoIndex > -1) {
      dispatch(updateHyperLinkSlide({
        ...slideDetails,
        slide: "",
      }))
      return;
    }

    if(spatialContext?.gating) {
      dispatch(updateHyperLinkSlide({
        page: "",
        slide: "",
        uid: "",
      }));
    }

    if(slideDetails.page !== activePageId) {
      spatialContext.navigateToPage(slideDetails.page, fullscreenRef.current, 0);
    }
  }, [slideDetails, loading, fullscreenRef.current, activePageId, spatialContext?.gating]);

  useEffect(() => {
    try {
      if(widgetSlideIndex > -1 && slideDetails.page === activePageId && slideDetails.page === page?.id) {
        scrollToSlide(widgetSlideIndex);
        dispatch(updateHyperLinkSlide({
          page: "",
          slide: "",
          uid: "",
        }));
      }
    } catch {}
  }, [slideDetails, activePageId, page?.id, slideWidgets]);
  
  // If carouselSlide.carouselUid may change and editorWidgets/slideWidgets are async, guard carefully
  useEffect(() => {
    if (!carouselSlide?.carouselUid || !Array.isArray(editorWidgets) || !Array.isArray(slideWidgets)) return;
  
    const widget = editorWidgets.find(d => d.uid === carouselSlide.carouselUid);
    if (!widget) return;
  
    const slideIndex = slideWidgets.findIndex(d => d.uid === widget.parent);
    if(slideIndex === currentSlideIndexRef.current) return;

    if (slideIndex > -1) {
      scrollToSlide(slideIndex);
    }
  }, [carouselSlide?.carouselUid, editorWidgets, slideWidgets, currentSlideIndexRef]);
  
  useEffect(()=>{
    if(gatingContext.gating && !gatingContext.preview) {
      checkGatingScreenVisible()
    }
  },[activePageRef.current.id])

  let isScrolling = false;

  useEffect(() => {
    const carousel = spatialPageRef.current;
    carousel.addEventListener('wheel', handleScroll);
    carousel.addEventListener('touchstart', handleTouchStart);
    carousel.addEventListener('touchmove', handleTouchMove);
    carousel.addEventListener('touchend', handleTouchEnd);

    return () => {
      carousel.removeEventListener('wheel', handleScroll);
      carousel.removeEventListener('touchstart', handleTouchStart);
      carousel.removeEventListener('touchmove', handleTouchMove);
      carousel.removeEventListener('touchend', handleTouchEnd);
    }
  }, [])

  const checkGatingScreenVisible =()=>{
    if(gatingContext.configuration){
      const gatingTargetPage = _.get(gatingContext, 'configuration.target.id');
      if( gatingTargetPage !== activePageRef.current.id){
        spatialContext.navigateToPage(gatingTargetPage, fullscreenRef.current, 0);
      }
    }
  }

  useEffect(()=>{
    spatialContext.triggeredGating(gatingContext.gating)
  },[gatingContext.gating])

  const didMountRef = useRef(false);
  const fullScreenClicked = useRef(false);
  useEffect(() => {
    fullscreenRef.current = spatialContext.fullscreen;
    if (didMountRef.current) {
      fullScreenClicked.current=true;
    } else {
      didMountRef.current = true; // Set to true after the initial render
    }
  }, [spatialContext.fullscreen]);


  React.useEffect(() => {
    if (gatingConfigurations.length > 0 || slideWidgetsRef.current.length > 0) {
      triggerSectionGating()
    }
  }, [gatingConfigurations, activePageRef.current.id, slideWidgetsRef.current])



  const triggerSectionGating = () => {
    if (gatingContext.preview) {
      return
    }

    var sectionConfigurations = gatingConfigurations.filter(
      (item) =>
        _.get(item, 'trigger.type', '') === 'SPATIAL_SLIDE' ||
        _.get(item, 'trigger.type', '') === 'TIME' ||
        _.get(item, 'trigger.type', '') === 'PERCENTAGE'
    )

    sectionConfigurations.every((item) => {
      var targetPage = _.get(item, 'target.id', 0)
      var slide = _.get(item, 'trigger.value', 0)
      const activeSlide = slideWidgetsRef.current[currentSlideIndexRef.current];

      if (targetPage === page?.id && activeSlide && slide === activeSlide.uid) {

        var gating = _.get(item, 'gating.id', null)

        if (gating) {
          gatingContext.triggerGating(_.get(item, 'trigger.type', ''), null, item)
          return false
        }
      }

      return true
    })
  }


  useEffect(() => {
    const type = _.get(gatingContext, 'type', '');
    if ((type === "SPATIAL_SLIDE" || type === "TIME" || type === "PERCENTAGE") && gatingContext.configuration){
      return;
    }

    const carousel = spatialPageRef.current;
    if(carousel) {
      const spatialPage = document.getElementById(`cs-spatial-page-${spatialContext.activePage?.id}`);
      if(spatialPage) {
        const spatialPageRect = spatialPage.getBoundingClientRect();
        carousel.style.transform = `translateY(${spatialPageRect.height * -spatialContext.activePage.slideIndex}px) translateZ(0)`;
      }
    }
  }, [fullscreenRef.current]);

  useEffect(() => {
    slideWidgetsRef.current = slideWidgets;
  }, [slideWidgets]);

  useEffect(() => {
    activePageRef.current = spatialContext.activePage;
    if(fullScreenClicked.current){
    currentSlideIndexRef.current = 0;}
    if(slideWidgetsRef.current.length == 1) {
      spatialContext.onChange('progress', {
        id: activePageRef.current.id,
        progress: 100
      });
    }
    fullScreenClicked.current=false;
  }, [spatialContext.activePage]);

  const scrollToSlide = (index) => {
    if (index < 0 || index >= slideWidgetsRef.current.length) {
      return;
    }

    const type = _.get(gatingContext, 'type', '');
    if ((type === "SPATIAL_SLIDE" || type === "TIME" || type === "PERCENTAGE") && gatingContext.configuration){
      return;
    }

    const carousel = spatialPageRef.current;
    const spatialPage =  document.getElementById(`cs-spatial-page-${spatialContext.activePage?.id}`);
    const spatialPageRect = spatialPage?.getBoundingClientRect();

    if(spatialPageRect) {
      spatialContext.navigateToPage(activePageRef.current.id, fullscreenRef.current, index).then(() => {
        carousel.style.transform = `translateY(${spatialPageRect.height * -index}px)`;
        currentSlideIndexRef.current = index;

        const percentage = ((index + 1) / slideWidgetsRef.current.length) * 100;

        if (spatialContext.onChange) {
          spatialContext.onChange('progress', {
            id: activePageRef.current.id,
            progress: percentage
          });
        }
      });
    }
  };

  const handleTouchStart = (event) => {
    startYRef.current = event.touches[0].pageY;
  };

  const handleTouchMove = (event) => {
    event.preventDefault();
    if (isScrolling) return;
    const currentY = event.touches[0].pageY;
    const deltaY = startYRef.current - currentY;


    if(breakpoint === 'mobile' && !fullscreenRef.current && event.deltaY > 0){
      spatialContext.onToggleFullscreen();
      setTimeout(() => {
        isScrolling = false;
      }, 1000);
      return;
    }


    if (Math.abs(deltaY) > 50) { // Adjust this threshold as needed
      isScrolling = true;

      if (deltaY > 0) {
        scrollToSlide(currentSlideIndexRef.current + 1);
      } else {
        scrollToSlide(currentSlideIndexRef.current - 1);
      }

      setTimeout(() => {
        isScrolling = false;
      }, 1000); // Adjust this timeout as needed
    }
  };

  const handleTouchEnd = () => {
    startYRef.current = 0;
  };


  const handleScroll = (event) => {
      event.preventDefault();
      if (isScrolling) return;
      const scrollThreshold = 12;
      if (Math.abs(event.deltaY) < scrollThreshold) return;
      isScrolling = true;
      if (event.deltaY > 0) {
          scrollToSlide(currentSlideIndexRef.current + 1);
      } else {
          scrollToSlide(currentSlideIndexRef.current - 1);
      }
      setTimeout(() => {
        isScrolling = false;
      }, 1000);
  };

  const handlePageClick = (e) => {
    e.preventDefault();
    spatialContext.onToggleFullscreen();
  }

  return (
    <motion.div
      initial={{ opacity: 1 }}
      animate={{ opacity: spatialContext.showMenu ? 0 : 1 }}
      id={page && page.id}
      className={styles.spatialPage}>
      <div
        id={`cs-spatial-page-${page?.id}`}
        className={cx(styles.spatialPageInner, 'spatialConatiner',
          { [styles.highlighted]: spatialContext.showHighlight },
          { [styles.fullscreen]: spatialContext.fullscreen })}
        style={{
          width: `${spatialContext.dimensions.width}px`,
          height: `${spatialContext.dimensions.height}px`,
          transformOrigin: 'center top'
        }}>
          {loading &&
            <LoadingMessage />
          }
          {slideBackground && <BackgroundViewComponent background={slideBackground}/>}
          {(breakpoint === 'mobile' && !spatialContext.fullscreen) &&
              <div onClick={handlePageClick} className={styles.mobileOverlay}></div>
          }
          <div id='cs-spatial-slide' ref={spatialPageRef} className={cx(styles.spatialPageContainer)}>
            {children}
          </div>
      </div>
    </motion.div>
  )
});

SpatialContent.SpatialPage = SpatialPage;

const LoadingMessage = () => {

  return (
    <div className={styles.loadingMessage}>
      <span className={styles.loadingDot}></span>
      <span className={styles.loadingDot}></span>
      <span className={styles.loadingDot}></span>
    </div>
  )
}

/*
 * Background manager
 */
function BackgroundManager({ background, imageCDN, backgroundPosition, width, mobile }) {
  const color = _.get(background, 'color');
  const blur = _.get(background, 'blur', 0);
  const colorOverlay = getColorsConfig(color);
  var opacity = _.get(background, 'opacity', 100) / 100;

  if (!background) {
    return (
      <div
        style={{ background: '#ffffff' }}
        className={styles.customBackground}
      />
    )
  }

  switch ( _.get(background, 'media.type', null) || background.type) {
    case 'color': {
      return (
        <div
          style={{ background: colorToString(background.color) }}
          className={styles.customBackground}
        />
      )
    }

    case 'image': {
      const media = _.get(background, 'media');
      const scale = _.get(background, 'scale', SCALE.SCALE_TO_FILL);
      var url = `${imageCDN}/tr:q-80/${media.url}`;
      if (width > 0) {
        if(scale === SCALE.TILED) {
          url = `${imageCDN}/tr:q-80/${media.url}`;
        } else {
          url = `${imageCDN}/tr:q-80/${media.url}`;
          if (mobile){
            url = `${imageCDN}/tr:dpr-2,q-80/${media.url}`;
          }
        }
      }

      return (
        <div className={styles.backgroundOverlay}>
          <div className={cx(styles.overlay, styles.color)} style={{
            background: colorOverlay,
            backdropFilter: `blur(${blur}px)`
          }}></div>
          <img style={{ transform: `translateX(${backgroundPosition}px)`, opacity: opacity }}
            id='cs-spatial-bg'
            className={styles.backgroundImage}
            src={url} />
        </div>
      )
    }

    case 'video': {
      return (
        <div className={styles.customBackground}>
          <div className={cx(styles.overlay, styles.color)} style={{
            background: colorOverlay,
            backdropFilter: `blur(${blur}px)`
          }}></div>
          <VideoView background={background} imageCDN={imageCDN} />
        </div>
      )
    }

    default:
      return (
        <div
          style={{ background: '#ffffff' }}
          className={styles.customBackground}
        />
      )
  }
}

const LeftArrow = React.memo(({ pages, activePage, menuDesigns, iconStyle, onClick }) => {
  const currentIndex = pages.findIndex(obj => obj.id === activePage.id);
  const isDisabled = currentIndex === 0;

  const regularStyle = useMemo(() => getStyles(menuDesigns, 'regular'), [menuDesigns]);
  const hoverStyle = useMemo(() => getStyles(menuDesigns, 'hover'), [menuDesigns]);

  const [isHovered, setIsHovered] = useState(false);

  const buttonStyle = useMemo(() => {
    if (isDisabled) return regularStyle;
    return isHovered ? hoverStyle : regularStyle;
  }, [isHovered, isDisabled, regularStyle, hoverStyle]);

  return (
    <Button
      variant="info"
      id="leftArrow"
      disabled={isDisabled}
      style={buttonStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      className={styles.arrowButton}
    >
      <i
        style={{ color: iconStyle.color }}
        className={cx(styles.icon, "cleverstory-drop-down-left")}
      />
    </Button>
  );
});

const RightArrow = React.memo(({ pages, activePage, menuDesigns, iconStyle, onClick }) => {
  const currentIndex = pages.findIndex(obj => obj.id === activePage.id);
  const isDisabled = currentIndex === pages.length - 1;

  const regularStyle = useMemo(() => getStyles(menuDesigns, 'regular'), [menuDesigns]);
  const hoverStyle = useMemo(() => getStyles(menuDesigns, 'hover'), [menuDesigns]);

  const [isHovered, setIsHovered] = useState(false);

  const buttonStyle = useMemo(() => {
    if (isDisabled) return regularStyle;
    return isHovered ? hoverStyle : regularStyle;
  }, [isHovered, isDisabled, regularStyle, hoverStyle]);

  return (
    <Button
      variant="info"
      id="rightArrow"
      disabled={isDisabled}
      style={buttonStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      className={styles.arrowButton}
    >
      <i
        style={{ color: iconStyle.color }}
        className={cx(styles.icon, "cleverstory-drop-down-right")}
      />
    </Button>
  );
});


/*
 * Menu
 */
const Menu = React.memo(({
  menuPosition,
  onToggleHighlight,
  onToggleGridMenu,
  appearance,
  onToggleFullscreen,
  fullscreen,
  showGridMenu,
  activePage,
  navigateToPage,
  breakpoint,
  pages
}) => {

  const ref = useRef(null);
  const scrollRef = useRef(null);
  const spatialContext = React.useContext(SpatialContext);
  const desktop = useDesktop();

  const firstPage = pages[0];
  const menuDesigns =  _.get(appearance, 'menuDesign');

  const [state, setState] = useState({
    show: false,
    menuPosition: menuPosition,
    containerStyle: {},
    buttonStyle: {},
    iconStyle: {}
  })


  const menuStyles = useMemo(() => ({
    containerStyle: getWrapperStyles(_.get(appearance, 'menuDesign')),
    buttonStyle: getStyles(_.get(appearance, 'menuDesign'), 'regular'),
    iconStyle: getIconStyles(_.get(appearance, 'menuDesign'))
  }), [appearance]);


  useEffect(() => {
    if (menuPosition.y === 0) {
      return;
    }

    setTimeout(() => {
      setState(state => {
        return {
          ...state,
          show: true
        }
      })
    }, 1000)
  }, [menuPosition.y]);

  useEffect(() => {
    const pageIndex = pages.findIndex(obj => obj.id === activePage.id);

    if (scrollRef.current && !scrollRef.current.isItemVisible(pageIndex)) {
      const item = scrollRef.current.getItemByIndex(pageIndex);
      scrollRef.current.scrollToItem(item)
    }
  }, [activePage])

  var y = showGridMenu || spatialContext.gating ? '100vh' : `${menuPosition.y}px`
  var style = {
    transform: `translateX(${breakpoint === 'mobile' && fullscreen ? 0 : menuPosition.x }px) translateY(${y})`,
  }

  useEffect(()=>{
    setState((state)=>{
      return {
        ...state,
        show: spatialContext.gating ? false : true
      }
    })
  },[spatialContext.gating])

  const handleLeftArrowClick = useCallback(() => {
    const previousIndex = findIndex(activePage.id, 'previous');
    if (previousIndex < 0) {
      return;
    }

    const currentPage = pages[previousIndex];

    if (scrollRef.current && !scrollRef.current.isItemVisible(previousIndex)) {
      const item = scrollRef.current.getItemByIndex(previousIndex);
      scrollRef.current.scrollToItem(item)
    }

    navigateToPage(currentPage.id, fullscreen, 0);
  }, [activePage.id, pages, fullscreen, navigateToPage]);

  const handleRightArrowClick = useCallback(() => {
    const nextIndex = findIndex(activePage.id, 'next');
    if (nextIndex >= pages.length) {
      return;
    }

    const currentPage = pages[nextIndex];

    if (scrollRef.current && !scrollRef.current.isItemVisible(nextIndex)) {
      const item = scrollRef.current.getItemByIndex(nextIndex);
      scrollRef.current.scrollToItem(item)
    }

    navigateToPage(currentPage.id, fullscreen, 0)
  }, [activePage.id, pages, fullscreen, navigateToPage]);


  const findIndex = (currentId, direction = 'next') => {
    const currentIndex = pages.findIndex(obj => obj.id === currentId);

    if (currentIndex === -1) {
      return -1;
    }

    let newIndex;
    if (direction === 'next') {
      newIndex = currentIndex + 1;
    } else if (direction === 'previous') {
      newIndex = currentIndex - 1;
    } else {
      throw new Error("Invalid direction. Use 'next' or 'previous'.");
    }

    return newIndex;
  }

  const handleHomeClick = () => {
    navigateToPage(firstPage.id, false, 0);
  }

  const MenuItem = React.memo(({ title, activePage, itemId, page }) => {
    const isActive = activePage.id === page.id;
  
    const regularStyle = useMemo(() => getStyles(menuDesigns, 'regular'), [menuDesigns]);
    const hoverStyle = useMemo(() => getStyles(menuDesigns, 'hover'), [menuDesigns]);
  
    const ref = useRef(null);
  
    const applyHover = (style) => {
      if (ref.current) {
        Object.assign(ref.current.style, style);
      }
    };
  
    return (
      <div
        ref={ref}
        role="menuitem"
        id={page.id}
        tabIndex={0}
        key={itemId}
        style={isActive ? hoverStyle : regularStyle}
        onMouseEnter={() => applyHover(hoverStyle)}
        onMouseLeave={() => applyHover(isActive ? hoverStyle : regularStyle)}
        onClick={() => navigateToPage(page.id, false, 0)}
        className={cx(styles.menuItemButton)}
      >
        <h3 className={styles.menuItemTitle}>
          <TruncatedTextWithTooltip content={title} maxWidth={150} />
        </h3>
      </div>
    );
  });
  
  return (
    <div id='cs-spatial-menu' ref={ref} style={style}
      className={cx(styles.toolbar,
      { [styles.show]: state.show },
      { [styles.fullscreen]: fullscreen })}>

      {!desktop && fullscreen &&
        <div style={menuStyles.containerStyle} className={cx(styles.mainMenu, styles.exitFullscreen)}>
          {breakpoint === 'mobile' &&
            <div
              id='fullscreenToggle'
              className={cx(styles.menuIcon)}
              onClick={onToggleFullscreen}
            >
              <i style={{color: menuStyles.iconStyle.color}} className={cx(styles.icon, 'cleverstory-drop-down-left')} />
            </div>
          }
          {
            <style>
              {`
                #fullscreenToggle i::before {
                  color: ${ getColorsConfig(menuStyles.iconStyle.color)};
                }
              `}
            </style>
          }
        </div>
      }

      <div style={menuStyles.containerStyle} className={styles.mainMenu}>
        {desktop &&
          <div style={{ maxWidth: '700px', overflow: 'hidden' }}>
            <ScrollMenu
              apiRef={scrollRef}
              scrollContainerClassName={styles.scrollingMenu}
              wrapperClassName={styles.scrollWrapper}
              LeftArrow={
                <LeftArrow 
                  pages={pages}
                  activePage={activePage}
                  menuDesigns={menuDesigns}
                  iconStyle={menuStyles.iconStyle}
                  onClick={handleLeftArrowClick}
                />
              }
              RightArrow={
                <RightArrow 
                  pages={pages}
                  activePage={activePage}
                  menuDesigns={menuDesigns}
                  iconStyle={menuStyles.iconStyle}
                  onClick={handleRightArrowClick}
                />
              }
            >
              {pages && pages.map((page, index) => (
                <MenuItem
                  itemId={index}
                  activePage={activePage}
                  key={index}
                  page={page}
                  title={page.name}
                />
              ))}
            </ScrollMenu>
          </div>
        }
        {!desktop &&
          <React.Fragment>
            <LeftArrow 
              pages={pages}
              activePage={activePage}
              menuDesigns={menuDesigns}
              iconStyle={menuStyles.iconStyle}
              onClick={handleLeftArrowClick}
            />
            <RightArrow 
              pages={pages}
              activePage={activePage}
              menuDesigns={menuDesigns}
              iconStyle={menuStyles.iconStyle}
              onClick={handleRightArrowClick}
            />
          </React.Fragment>
        }
      </div>

      <div style={menuStyles.containerStyle} className={styles.mainMenu}>
        {desktop &&
          <div
            className={styles.menuIcon}
            onClick={onToggleGridMenu}
          >
            <i style={{color: menuStyles.iconStyle.color}} className={cx(styles.icon, 'cleverstory-grid')} />
          </div>
        }
        {desktop &&
          <div
            className={styles.menuIcon}
            onClick={onToggleFullscreen}
          >
            <i style={{color: menuStyles.iconStyle.color}} className={cx(styles.icon, 'cleverstory-expand-editor')} />
          </div>
        }
        {!desktop &&
          <div
            className={cx(styles.menuIcon)}
            onClick={handleHomeClick}
          >
            <span style={{color: menuStyles.iconStyle.color}} className={cx(styles.icon, 'cleverstory-sptial-home')}>
              <span className="path1"></span>
            </span>
          </div>
        }
        {/* {breakpoint === 'desktop' &&
          <div className={styles.menuIcon} onClick={onToggleHighlight}>
            <i className={cx(styles.icon, 'cleverstory-settings')} />
          </div>
        } */}
        {/* <div className={styles.menuIcon}>
          <i className={cx(styles.icon, 'cleverstory-more')} />
        </div> */}
        {
          <style>
              {`
                .menuIcon i::before {
                  color: ${ getColorsConfig(menuStyles.iconStyle.color)};
                }
              `}
          </style>
        }
      </div>
    </div>
  )
})



/**
 * Generate Styles
 *
*/
function getWrapperStyles(properties) {
  var styles = {};

  if (!properties) {
    return styles;
  }


  //background
  const backgroundRgb = _.get(properties, `background`, null);
  var background = 'inherit';
  if (backgroundRgb) {
    background = getColorsConfig(backgroundRgb);
  }

  styles['background'] = background;


  //blur
  const blur = _.get(properties, `background.blur`, null);
  var blurEffect = 20;
  if (blur) {
    blurEffect = blur;
  }
  styles['-webkit-backdrop-filter'] = `blur(${blurEffect}px)`;
  styles['backdrop-filter'] = `blur(${blurEffect}px)`;

  return styles;
}



/**
 * Generate Styles
 *
*/
function getIconStyles(properties) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultStyles = _.get(properties, `regular`, {});

  //font-color
  const colorRgb = _.get(defaultStyles, `font.color`, null);
  const color = getColorStyles(colorRgb);
  styles = {
    ...styles,
    ...color
  }

  return styles;
}


/**
 * Generate Styles
 *
*/
function getStyles(properties, active) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultStyles = _.get(properties, `regular`, {});

  //font-color
  const colorRgb = _.get(defaultStyles, `font.color`, null);
  const color = getColorStyles(active == 'regular' ? {...colorRgb, a: 0.60} :colorRgb);
  styles = {
    ...styles,
    ...color
  }

  //background
  const backgroundRgb = _.get(properties, `${active}.background`, null);
  var background = 'inherit';
  if (backgroundRgb && active == 'hover') {
    background = getColorsConfig(backgroundRgb);
  }

  styles['background'] = background;

  const font = _.get(defaultStyles, 'font', {});

  //font-family
  const fontFamily = _.get(font, 'fontFamily');
  if (fontFamily) {
    styles = {
      ...styles,
      fontFamily: injectAktivFallback(fontFamily)
    }
  }

  return styles;
}

export function getColorStyles(value) {
  if (!value) {
    return null;
  }

  const colorRgb = value;

  var color = 'inherit';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  var styles = {
    color: color
  };

  return styles;
}

/*
 * Grid Menu
 */
const GridMenu = React.memo(({ pages, show, onClose, fullscreen, onSelect }) => {

  const handleSelect = (id) => {
    onSelect(id, fullscreen);
    onClose();
  }

  return (
    <motion.div
      className={cx(styles.gridMenu, {
        [styles.open]: show,
      })}
      initial={{ opacity: 0, zIndex: -1}}
      animate={{ opacity: show ? 1 : 0, zIndex: show ? 1 : -1}}
    >
      <div className={styles.menuHeader}>
        <h1>Contents </h1>
      </div>
      <div className={styles.menuItems}>
        {pages.map((page, index) => {
            return (
              <motion.div
              key={index}
              className={styles.gridItem}
              onClick={handleSelect.bind(this, page.id)}
            >
              <div className={styles.menuIcon}>
                <div className={styles.glassEffect}/>
                <IKImage path={_.get(page, 'icon.url')} />
              </div>
              <h3 className={styles.menuTitle}>
                <TruncatedTextWithTooltip content={page.name}  maxWidth={250}/>
              </h3>
            </motion.div>
            )
          })}
      </div>
      <div className={styles.menuFooter}>
        <button className={styles.closeButton} onClick={onClose}>
          <i className={cx(styles.closeButtonIcon, 'cleverstory-close')} />
        </button>
      </div>
    </motion.div>
  );
});

/*
 * Key Manager
 */
function KeyManager({ children }) {
  const containerRef = React.useRef()
  const spatialContext = React.useContext(SpatialContext)

  const pages = spatialContext.pages;

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.focus()
    }
  }, [])

  const keyMap = {
    MOVE_LEFT: 'left',
    MOVE_RIGHT: 'right',
    MOVE_DOWN: 'down',
    MOVE_UP: 'up',
    ESCAPE: 'esc'
  }

  const navigateNext = React.useCallback(
    (e) => {
      e.preventDefault();

      const currentIndex = pages.findIndex(item => item.id === spatialContext.activePage.id);

      if(currentIndex >= pages.length - 1) {
        return;
      }

      const nextPage = pages[currentIndex + 1];

      spatialContext.navigateToPage(
        nextPage.id,
        spatialContext.fullscreen,
        0
      )
    },
    [spatialContext.activePage]
  )

  const navigatePrevious = React.useCallback(
    (e) => {
      e.preventDefault()

      const currentIndex = pages.findIndex(item => item.id === spatialContext.activePage.id);

      if(currentIndex <= 0) {
        return;
      }

      const previousPage = pages[currentIndex - 1];

      spatialContext.navigateToPage(
        previousPage.id,
        spatialContext.fullscreen,
        0
      )
    },
    [spatialContext.activePage]
  )

  const toggleFullScreen = React.useCallback(
    (e) => {
      spatialContext.toggleFullScreen
      spatialContext.toggleGridMenu
    },
    [spatialContext.activePage]
  )

  return (
    <GlobalHotKeys
      className={styles.keymanager}
      innerRef={containerRef}
      keyMap={ spatialContext.gating ? {} : keyMap}
      handlers={{
        MOVE_UP: toggleFullScreen,
        MOVE_DOWN: toggleFullScreen,
        MOVE_LEFT: navigatePrevious,
        MOVE_RIGHT: navigateNext,
        ESCAPE: toggleFullScreen
      }}
      allowChanges
    >
      {children}
    </GlobalHotKeys>
  )
}

/*
 * Highlight
 */
const Highlight = ({ position, show, scale, showGridMenu }) => {

  var x = _.get(position, 'x', 0);
  var y = _.get(position, 'y', 0);
  var style = {
    transform: `scaleX(${scale}) translateX(${!show || showGridMenu ? x + 300 : x}px) translateY(${y}px)`,
    transformOrigin: 'right center'
  }

  return (
    <div id='cs-spatial-highlight' style={style} className={cx(styles.highlight, { [styles.show]: show })}>
    </div>
  )
}

function useViewportSize() {
  const [viewportSize, setViewportSize] = useState({
    width: 0,
    height: 0,
    viewportHeight: '100vh'
  });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        const vh = (window.visualViewport ? window.visualViewport.height : window.innerHeight) * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
        document.body.style.height = `${vh * 100}px`;

        setViewportSize({
          width: window.visualViewport?.width || window.innerWidth,
          height: window.visualViewport?.height || window.innerHeight,
          viewportHeight: `${vh * 100}px`
        });
      }
    };

    handleResize();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  // Return default values during SSR
  if (!mounted) {
    return {
      width: 1200,
      height: 800,
      viewportHeight: '100vh'
    };
  }

  return viewportSize;
}